import React, { useEffect, useState } from "react";
import {
  Card,
  Space,
  Row,
  Col,
  notification,
  Typography,
  Spin,
  Tag,
  Modal,
  Table,
  TableColumnsType,
  Button,
  message,
  Tooltip,
  Dropdown,
  Menu,
} from "antd";
import { useParams, Link } from "react-router-dom";
import { TestRunService } from "../../services/TestRunService";
import { usePermissions } from "../../PermissionsProvider";
import moment from "moment";
import {
  TestSet,
  TestRun,
  TestRunRequest,
  TestRunQualitativeEvaluationSummary,
  IntentGroup,
} from "../../model_gen/evaluation";
import ConfigurationPanel from "../Application/configuration/ConfigurationPanel";
import { CopyOutlined, DiffOutlined, MoreOutlined } from "@ant-design/icons";
import { TestSetService } from "../../services/TestSetService";
import { IntentService } from "../../services/IntentService";
import TestRunRequestComparison from "./TestRunRequestComparison";
import RequestRepresentationModal from "../Request/RequestRepresentationModal";
import { RequestViewButton } from "../shared/button/Buttons";

const { Text } = Typography;

const TestRunDetail: React.FC = () => {
  const { intentGroupId, testSetId, testRunId } = useParams();
  const { apiKey } = usePermissions();
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [testSet, setTestSet] = useState<TestSet | null>(null);
  const [testRun, setTestRun] = useState<TestRun | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isConfigModalVisible, setIsConfigModalVisible] = useState(false);
  const [requests, setRequests] = useState<TestRunRequest[]>([]);
  const [isRequestsLoading, setIsRequestsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [requestsByPage, setRequestsByPage] = useState<
    Record<number, TestRunRequest[]>
  >({});
  const [totalRequests, setTotalRequests] = useState<number>(0);
  const [testRunResults, setTestRunResults] =
    useState<TestRunQualitativeEvaluationSummary | null>(null);
  const [isResultsLoading, setIsResultsLoading] = useState<boolean>(false);
  const [selectedScores, setSelectedScores] = useState<number[]>([]);
  const [isComparisonModalVisible, setIsComparisonModalVisible] =
    useState(false);
  const [selectedComparisonRequestId, setSelectedComparisonRequestId] =
    useState<number | null>(null);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null
  );
  const [isRepresentationModalVisible, setIsRepresentationModalVisible] =
    useState(false);

  useEffect(() => {
    if (intentGroupId) {
      IntentService.getIntentGroup(
        Number(intentGroupId),
        false,
        setIsLoading,
        apiKey
      ).subscribe(
        (intentGroup: IntentGroup) => {
          setIntentGroup(intentGroup);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [intentGroupId]);

  useEffect(() => {
    if (testSetId) {
      TestSetService.getTestSet(testSetId, null, apiKey).subscribe(
        (data: TestSet) => {
          setTestSet(data);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch test set: ${error}`,
          });
        }
      );
    }
  }, [testSetId]);

  useEffect(() => {
    if (testRunId) {
      setIsLoading(true);
      TestRunService.getTestRun(
        Number(testRunId),
        setIsLoading,
        apiKey
      ).subscribe(
        (data: TestRun) => {
          setTestRun(data);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch test run: ${error}`,
          });
          setIsLoading(false);
        }
      );
    }
  }, [testRunId]);

  useEffect(() => {
    if (testRunId && !requestsByPage[currentPage]) {
      fetchRequests(currentPage);
    } else {
      setRequests(requestsByPage[currentPage]);
    }
  }, [testRunId, currentPage, requestsByPage, selectedScores]);

  useEffect(() => {
    if (testRunId) {
      setIsResultsLoading(true);
      TestRunService.getTestRunResults(
        Number(testRunId),
        setIsResultsLoading,
        apiKey
      ).subscribe(
        (data: any) => {
          setTestRunResults(data);
          setIsResultsLoading(false);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch test run results: ${error}`,
          });
          setIsResultsLoading(false);
        }
      );
    }
  }, [testRunId]);

  const fetchRequests = (page: number) => {
    const offset = (page - 1) * 10;
    setIsRequestsLoading(true);
    TestRunService.getTestRunRequests(
      Number(testRunId),
      offset,
      10,
      setIsRequestsLoading,
      apiKey,
      selectedScores.length > 0 ? selectedScores : null
    ).subscribe(
      (response: { items: TestRunRequest[]; total: number }) => {
        setRequests(response.items);
        setTotalRequests(response.total);
        setRequestsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
        setIsRequestsLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch requests: ${error}`,
        });
        setIsRequestsLoading(false);
      }
    );
  };

  const handleRowClick = (record: TestRunRequest) => {
    console.log("Clicked TestRunRequest", record.id);
    // const sessionId = record.original_request?.session_id;
    // const requestId = record.original_request?.request_id;

    // if (applicationId && sessionId && requestId) {
    //   navigate(`/application/${applicationId}/session/${encodeURIComponent(sessionId)}/request/${requestId}`);
    // }
  };

  const handleScoreClick = (score: number) => {
    setSelectedScores((prevScores) => {
      const newScores = prevScores.includes(score)
        ? prevScores.filter((s) => s !== score)
        : [...prevScores, score];

      setRequestsByPage({});
      return newScores;
    });
  };

  // const handleOpenRepresentationModal = (record: TestRunRequest) => {
  //   setSelectedRequestId(record.original_request_id);
  //   setIsRepresentationModalVisible(true);
  // };

  const requestColumns: TableColumnsType<TestRunRequest> = [
    {
      title: () => <div style={{ textAlign: "left" }}>Request ID</div>,
      key: "request_id",
      width: "15%",
      align: "right",
      render: (_, record: TestRunRequest) => {
        const requestId = record.original_request_id || "";
        const shortRequestId =
          requestId.length > 8 ? `...${requestId.slice(-8)}` : requestId;
        return (
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            {shortRequestId}
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success("Request ID copied to clipboard");
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: "15%",
      render: (_, record: TestRunRequest) => {
        const statusColor =
          {
            CREATED: "default",
            PENDING: "processing",
            COMPLETED: "success",
            ERROR: "error",
          }[record.status] || "default";

        return <Tag color={statusColor}>{record.status}</Tag>;
      },
    },
    {
      title: "Tags",
      key: "tags",
      width: "20%",
      render: (_, record: TestRunRequest) => {
        try {
          const metaData = JSON.parse(
            (record.original_request?.metadata
              ?._maitai_test_request_meta as string) || "{}"
          );
          const tags = metaData.tags || [];
          return (
            <Space size={[0, 8]} wrap>
              {tags.map((tag: string) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </Space>
          );
        } catch (e) {
          return null;
        }
      },
    },
    {
      title: "Score",
      key: "score",
      width: "15%",
      render: (_, record: TestRunRequest) => {
        const results = record.qualitative_evaluation_results || [];
        if (results.length === 0) return null;

        const maxPossibleScore = results.length * 5; // Each result can score max 5
        const totalScore = results.reduce(
          (sum, result) => sum + (result.score_value || 0),
          0
        );
        const normalizedScore = Math.round(
          (totalScore / maxPossibleScore) * 100
        );

        const getScoreColor = (score: number) => {
          if (score >= 80) return "#52c41a"; // green
          if (score >= 60) return "#faad14"; // yellow
          return "#f5222d"; // red
        };

        return (
          <Tooltip
            title={
              <div>
                {results.map((result, idx) => (
                  <div key={idx} style={{ marginBottom: "8px" }}>
                    {result.criteria}: {result.score_value}/5
                    {result.description && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#ccc",
                        }}>
                        {result.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            }>
            <Tag
              style={{
                color: getScoreColor(normalizedScore),
                borderColor: getScoreColor(normalizedScore),
                background: "transparent",
              }}>
              {normalizedScore}%
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Time",
      key: "time",
      width: "10%",
      render: (_, record: TestRunRequest) => {
        return record.response?.response_time ? (
          <Tooltip title={`Response time: ${record.response.response_time}ms`}>
            <Text>{record.response.response_time}ms</Text>
          </Tooltip>
        ) : null;
      },
    },
    {
      title: "",
      key: "actions",
      width: "20%",
      render: (_, record: TestRunRequest) => {
        const compareTooltip = !record.original_request ? (
          "No request found"
        ) : (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "900px",
              maxWidth: "900px",
              boxSizing: "border-box",
            }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                marginTop: "12px",
              }}>
              <div>
                <strong style={{ marginLeft: "12px", marginBottom: "8px" }}>
                  Original Response
                  {record.original_request.metadata
                    ._maitai_test_request_response && "*"}
                </strong>
                <div
                  style={{
                    padding: "12px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}>
                  {(record.original_request.metadata
                    ._maitai_test_request_response as string) ||
                    (record.original_request?.params &&
                      record.original_request.params.messages[
                        record.original_request.params.messages.length - 1
                      ]?.content) ||
                    "No response"}
                </div>
              </div>
              <div>
                <strong style={{ marginLeft: "12px", marginBottom: "8px" }}>
                  Test Response
                </strong>
                <div
                  style={{
                    padding: "12px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}>
                  {record.response?.choices?.[0]?.message?.content ||
                    "No response"}
                </div>
              </div>
            </div>
          </div>
        );

        return (
          <Space>
            <RequestViewButton
              apiKey={apiKey}
              request={record.original_request}
              to={`/application/${
                record.original_request.application_id
              }/session/${encodeURIComponent(
                record.original_request.session_id
              )}/request/${record.original_request.request_id}`}
              systemPrompt={testRun?.config?.system_prompt}
              buttonStyle={{ color: "#21b892" }}
              testSetId={testSetId}
              testSetRequestId={
                record.original_request.metadata
                  ._maitai_test_request_id as string
              }>
              Request
            </RequestViewButton>
            <Tooltip
              title={compareTooltip}
              placement="left"
              overlayStyle={{ maxWidth: "900px", width: "900px" }}>
              <Button
                type="link"
                icon={<DiffOutlined />}
                onClick={(e) => e.stopPropagation()}>
                Responses
              </Button>
            </Tooltip>
            <Dropdown
              trigger={["click"]}
              overlay={
                <Menu
                  onClick={(info) => {
                    if (info.key === "compare") {
                      setSelectedComparisonRequestId(record.id || null);
                      setIsComparisonModalVisible(true);
                    } else if (info.key === "representation") {
                      // handleOpenRepresentationModal(record);
                    }
                  }}>
                  <Menu.Item key="compare">Compare</Menu.Item>
                  {/* <Menu.Item key="representation">Representation</Menu.Item> */}
                </Menu>
              }>
              <Button
                type="link"
                icon={<MoreOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}>
          <Col>
            <Typography style={{ fontSize: "24px", fontWeight: "600" }}>
              {!intentGroup || !testSet || !testRun ? (
                <Spin size="small" />
              ) : (
                <>
                  <Link to={"/intent-groups"} style={{ color: "#828282" }}>
                    Intent Groups
                    {" / "}{" "}
                  </Link>
                  <Link
                    to={`/intent-group/${intentGroup.id}`}
                    style={{ color: "#828282" }}>
                    {intentGroup.intent_name}
                    {" / "}{" "}
                  </Link>
                  <Link
                    to={`/intent-group/${intentGroup.id}/testsets`}
                    style={{ color: "#828282" }}>
                    Test Sets
                    {" / "}{" "}
                  </Link>
                  <Link
                    to={`/intent-group/${intentGroup.id}/testsets/${testSetId}`}
                    style={{ color: "#828282" }}>
                    {testSet.name}
                    {" / "}{" "}
                  </Link>
                  Test Run {testRun.id}
                </>
              )}
            </Typography>
          </Col>
        </Row>

        {testRun && (
          <Card
            size="small"
            title={
              <Text strong style={{ fontSize: "16px" }}>
                Details
              </Text>
            }
            style={{
              background: "#f8f9fa",
              borderRadius: "8px",
              marginBottom: "12px",
            }}
            loading={isLoading}>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Text type="secondary">Date Created</Text>
                <div>
                  <Text strong style={{ fontSize: "14px" }}>
                    {moment(testRun.date_created).format(
                      "MMM D, YYYY HH:mm:ss"
                    )}
                  </Text>
                </div>
              </Col>
              <Col span={8}>
                <Text type="secondary">Date Completed</Text>
                <div>
                  <Text strong style={{ fontSize: "14px" }}>
                    {testRun.date_completed
                      ? moment(testRun.date_completed).format(
                          "MMM D, YYYY HH:mm:ss"
                        )
                      : "-"}
                  </Text>
                </div>
              </Col>
              <Col span={8}>
                <Text type="secondary">Status</Text>
                <div>
                  <Tag
                    color={
                      testRun.status === "COMPLETED"
                        ? "success"
                        : testRun.status === "ERROR"
                        ? "error"
                        : testRun.status === "PENDING"
                        ? "processing"
                        : "default"
                    }>
                    {testRun.status}
                  </Tag>
                </div>
              </Col>
              <Col span={16}>
                <Text type="secondary">Description</Text>
                <div>
                  <Text strong style={{ fontSize: "14px" }}>
                    {testRun.description || "No description provided"}
                  </Text>
                </div>
              </Col>
              <Col span={8}>
                <Text type="secondary">Configuration</Text>
                <div>
                  <Text
                    strong
                    style={{
                      fontSize: "14px",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={() => setIsConfigModalVisible(true)}>
                    {testRun.config?.model || "No model specified"}
                  </Text>
                </div>
              </Col>
            </Row>
          </Card>
        )}

        {testRun && (
          <Card
            size="small"
            title={
              <Text strong style={{ fontSize: "16px" }}>
                Results
              </Text>
            }
            style={{
              background: "#f8f9fa",
              borderRadius: "8px",
              marginBottom: "12px",
            }}>
            {isResultsLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "24px",
                }}>
                <Spin />
              </div>
            ) : (
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Text type="secondary"># Requests</Text>
                  <div>
                    <Text strong style={{ fontSize: "14px" }}>
                      {testRunResults?.total_requests || 0}
                    </Text>
                  </div>
                </Col>
                <Col span={6}>
                  <Text type="secondary">Completed %</Text>
                  <div>
                    <Text strong style={{ fontSize: "14px" }}>
                      {testRunResults
                        ? `${Math.round(
                            (testRunResults.completed_requests /
                              testRunResults.total_requests) *
                              100
                          )}%`
                        : "-"}
                    </Text>
                  </div>
                </Col>
                <Col span={6}>
                  <Text type="secondary">Error %</Text>
                  <div>
                    <Text strong style={{ fontSize: "14px" }}>
                      {testRunResults
                        ? `${Math.round(
                            (testRunResults.error_requests /
                              testRunResults.total_requests) *
                              100
                          )}%`
                        : "-"}
                    </Text>
                  </div>
                </Col>
                <Col span={6}>
                  <Text type="secondary">Response Time</Text>
                  <div>
                    <Text strong style={{ fontSize: "14px" }}>
                      {testRunResults?.response_time_percentiles?.p50 ? (
                        <Tooltip
                          title={
                            <div>
                              <div>
                                P50:{" "}
                                {Math.round(
                                  testRunResults.response_time_percentiles.p50
                                )}
                                ms
                              </div>
                              <div>
                                P90:{" "}
                                {Math.round(
                                  testRunResults.response_time_percentiles.p90
                                )}
                                ms
                              </div>
                              <div>
                                P95:{" "}
                                {Math.round(
                                  testRunResults.response_time_percentiles.p95
                                )}
                                ms
                              </div>
                            </div>
                          }>
                          <span>
                            {Math.round(
                              testRunResults.response_time_percentiles.p50
                            )}
                            ms
                          </span>
                        </Tooltip>
                      ) : (
                        "-"
                      )}
                    </Text>
                  </div>
                </Col>
                <Col span={24}>
                  <Text type="secondary">Score Distribution</Text>
                  <div style={{ marginTop: "8px" }}>
                    {testRunResults?.request_scores?.length > 0 ? (
                      <>
                        <div
                          style={{
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "24px",
                            margin: "0 auto",
                          }}>
                          {(() => {
                            const totalScores =
                              testRunResults.request_scores.reduce(
                                (sum, item) => sum + item.count,
                                0
                              );
                            const neutralScore =
                              testRunResults.request_scores.find(
                                (item) => item.score === 3
                              );

                            // Calculate the actual neutral portion, but cap it at 5%
                            const actualNeutralPortion = neutralScore
                              ? neutralScore.count / totalScores
                              : 0;
                            const neutralWidth = Math.min(
                              actualNeutralPortion * 100,
                              5
                            );

                            // Calculate non-neutral total (excluding score 3)
                            const nonNeutralScores =
                              testRunResults.request_scores.filter(
                                (item) => item.score !== 3
                              );
                            const nonNeutralTotal = nonNeutralScores.reduce(
                              (sum, item) => sum + item.count,
                              0
                            );

                            // Each side gets 47.5% of the total width (since center takes 5%)
                            const sideWidth = (100 - neutralWidth) / 2;

                            return (
                              <div
                                style={{
                                  position: "relative",
                                  width: "100%",
                                  height: "100%",
                                }}>
                                {/* Left side container */}
                                <div
                                  style={{
                                    position: "absolute",
                                    right: `${50 + neutralWidth / 2}%`,
                                    height: "100%",
                                    width: `${sideWidth}%`,
                                    display: "flex",
                                    justifyContent: "flex-end",
                                  }}>
                                  {[
                                    {
                                      score: 1,
                                      label: "POOR (-2)",
                                      color: "#f5222d",
                                    },
                                    {
                                      score: 2,
                                      label: "FAIR (-1)",
                                      color: "#ffa39e",
                                    },
                                  ].map(({ score, label, color }) => {
                                    const scoreData =
                                      testRunResults.request_scores.find(
                                        (item) => item.score === score
                                      );
                                    const tooltipPercentage = scoreData
                                      ? (scoreData.count / totalScores) * 100
                                      : 0;
                                    // Calculate width as proportion of non-neutral scores * available side width
                                    const widthPercentage =
                                      scoreData && nonNeutralTotal > 0
                                        ? (scoreData.count / nonNeutralTotal) *
                                          100
                                        : 0;

                                    return widthPercentage > 0 ? (
                                      <Tooltip
                                        key={label}
                                        title={`${label}: ${Math.round(
                                          tooltipPercentage
                                        )}%`}>
                                        <div
                                          onClick={() =>
                                            handleScoreClick(score)
                                          }
                                          style={{
                                            width: `${widthPercentage}%`,
                                            backgroundColor: color,
                                            height: "100%",
                                            transition:
                                              "width 0.3s ease, box-shadow 0.3s ease",
                                            cursor: "pointer",
                                            opacity:
                                              selectedScores.length === 0 ||
                                              selectedScores.includes(score)
                                                ? 1
                                                : 0.5,
                                            boxShadow: selectedScores.includes(
                                              score
                                            )
                                              ? "0 0 0 2px #1890ff"
                                              : "none",
                                            position: "relative",
                                            zIndex: selectedScores.includes(
                                              score
                                            )
                                              ? 0
                                              : "auto",
                                          }}
                                        />
                                      </Tooltip>
                                    ) : null;
                                  })}
                                </div>

                                {/* Center container for GOOD scores */}
                                <div
                                  style={{
                                    position: "absolute",
                                    left: `${50 - neutralWidth / 2}%`,
                                    width: `${neutralWidth}%`,
                                    height: "100%",
                                    backgroundColor: "#d9d9d9",
                                    transition: "all 0.3s ease",
                                  }}>
                                  {neutralScore && neutralWidth > 0 && (
                                    <Tooltip
                                      title={`GOOD (0): ${Math.round(
                                        actualNeutralPortion * 100
                                      )}%`}>
                                      <div
                                        onClick={() => handleScoreClick(3)}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          cursor: "pointer",
                                          opacity:
                                            selectedScores.length === 0 ||
                                            selectedScores.includes(3)
                                              ? 1
                                              : 0.5,
                                          boxShadow: selectedScores.includes(3)
                                            ? "0 0 0 2px #1890ff"
                                            : "none",
                                          position: "relative",
                                          zIndex: selectedScores.includes(3)
                                            ? 0
                                            : "auto",
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </div>

                                {/* Right side container */}
                                <div
                                  style={{
                                    position: "absolute",
                                    left: `${50 + neutralWidth / 2}%`,
                                    height: "100%",
                                    width: `${sideWidth}%`,
                                    display: "flex",
                                  }}>
                                  {[
                                    {
                                      score: 4,
                                      label: "GREAT (+1)",
                                      color: "#b7eb8f",
                                    },
                                    {
                                      score: 5,
                                      label: "PERFECT (+2)",
                                      color: "#52c41a",
                                    },
                                  ].map(({ score, label, color }) => {
                                    const scoreData =
                                      testRunResults.request_scores.find(
                                        (item) => item.score === score
                                      );
                                    const tooltipPercentage = scoreData
                                      ? (scoreData.count / totalScores) * 100
                                      : 0;
                                    // Calculate width as proportion of non-neutral scores * available side width
                                    const widthPercentage =
                                      scoreData && nonNeutralTotal > 0
                                        ? (scoreData.count / nonNeutralTotal) *
                                          100
                                        : 0;

                                    return widthPercentage > 0 ? (
                                      <Tooltip
                                        key={label}
                                        title={`${label}: ${Math.round(
                                          tooltipPercentage
                                        )}%`}>
                                        <div
                                          onClick={() =>
                                            handleScoreClick(score)
                                          }
                                          style={{
                                            width: `${widthPercentage}%`,
                                            backgroundColor: color,
                                            height: "100%",
                                            transition:
                                              "width 0.3s ease, box-shadow 0.3s ease",
                                            cursor: "pointer",
                                            opacity:
                                              selectedScores.length === 0 ||
                                              selectedScores.includes(score)
                                                ? 1
                                                : 0.5,
                                            boxShadow: selectedScores.includes(
                                              score
                                            )
                                              ? "0 0 0 2px #1890ff"
                                              : "none",
                                            position: "relative",
                                            zIndex: selectedScores.includes(
                                              score
                                            )
                                              ? 0
                                              : "auto",
                                          }}
                                        />
                                      </Tooltip>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: "8px",
                            fontSize: "12px",
                            color: "#8c8c8c",
                          }}>
                          <span>Poor</span>
                          <div
                            style={{
                              position: "absolute",
                              left: "50%",
                              transform: "translateX(-50%)",
                              fontSize: "12px",
                              color: "#8c8c8c",
                            }}>
                            Neutral
                          </div>
                          <span>Perfect</span>
                        </div>
                      </>
                    ) : (
                      <Text>No data available</Text>
                    )}
                  </div>
                </Col>
              </Row>
            )}
          </Card>
        )}

        {/* Configuration Modal */}
        <Modal
          title="Test Run Configuration"
          open={isConfigModalVisible}
          onCancel={() => setIsConfigModalVisible(false)}
          footer={null}
          width={800}>
          <ConfigurationPanel
            intentGroupId={intentGroupId ? Number(intentGroupId) : null}
            config={testRun?.config}
            updateConfig={() => {}}
            resetConfig={() => {}}
            isModalOpen={true}
            readOnly={true}
            allowSecondaryModel={false}
            showSystemPrompt={true}
          />
        </Modal>

        {/* Add the Requests table */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}>
            <Text style={{ color: "#828282", fontSize: 16, fontWeight: 600 }}>
              Requests
            </Text>
            {selectedScores.length > 0 && (
              <Space align="center">
                <Space wrap>
                  {selectedScores.map((score) => {
                    const scoreLabels: Record<number, string> = {
                      1: "POOR (-2)",
                      2: "FAIR (-1)",
                      3: "GOOD (0)",
                      4: "GREAT (+1)",
                      5: "PERFECT (+2)",
                    };
                    const scoreColors: Record<number, string> = {
                      1: "#f5222d",
                      2: "#ffa39e",
                      3: "#d9d9d9",
                      4: "#b7eb8f",
                      5: "#52c41a",
                    };
                    return (
                      <Tag
                        key={score}
                        color={scoreColors[score]}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          handleScoreClick(score);
                        }}>
                        {scoreLabels[score]}
                      </Tag>
                    );
                  })}
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      setSelectedScores([]);
                      setRequestsByPage({});
                    }}>
                    Clear All
                  </Button>
                </Space>
              </Space>
            )}
          </div>
          <Table
            rowKey="test_run_request_id"
            columns={requestColumns}
            dataSource={requests}
            loading={isRequestsLoading}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: "pointer" },
            })}
            pagination={{
              current: currentPage,
              pageSize: 10,
              total: totalRequests,
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: false,
            }}
            style={{ marginTop: "16px" }}
          />
        </div>

        {/* Add the comparison modal */}
        {selectedComparisonRequestId && (
          <TestRunRequestComparison
            testRunId={Number(testRunId)}
            testRunRequestId={selectedComparisonRequestId}
            isVisible={isComparisonModalVisible}
            onClose={() => {
              setIsComparisonModalVisible(false);
              setSelectedComparisonRequestId(null);
            }}
            apiKey={apiKey}
            testSetId={testSetId}
            systemPrompt={testRun?.config?.system_prompt}
            intentGroupId={intentGroupId}
          />
        )}

        {/* Replace the old modal with the new component */}
        {selectedRequestId && (
          <RequestRepresentationModal
            requestId={selectedRequestId}
            isVisible={isRepresentationModalVisible}
            onClose={() => {
              setIsRepresentationModalVisible(false);
              setSelectedRequestId(null);
            }}
            apiKey={apiKey}
          />
        )}
      </Space>
    </Card>
  );
};

export default TestRunDetail;
